import { useState } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Plus, Trash2, CalendarDays, MapPin, CalendarIcon, Clock, Repeat, Pencil, Image as ImageIcon, Star, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { isVideoSrc, prepareMediaFile } from "@/lib/media";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const empty = { name: "", date: undefined, time: "", end_time: "", location: "", description: "", recurring: false, weekday: undefined, image_url: "", featured: false };

const WEEKDAYS_ES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const RECURRING_LABEL_ES = [
  "Todos los domingos",
  "Todos los lunes",
  "Todos los martes",
  "Todos los miércoles",
  "Todos los jueves",
  "Todos los viernes",
  "Todos los sábados",
];

function formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const h12 = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}

export default function EventsManager({ events, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [flyerOptions, setFlyerOptions] = useState([]);
  const [flyerVerse, setFlyerVerse] = useState(null);

  const generateFlyer = async () => {
    if (!form.name.trim()) {
      toast.error("Escribe primero el nombre del evento");
      return;
    }
    setGenerating(true);
    setFlyerOptions([]);
    setFlyerVerse(null);
    try {
      const dateLabel = form.recurring
        ? (form.weekday !== undefined && form.weekday !== null ? RECURRING_LABEL_ES[Number(form.weekday)] : "")
        : form.date
          ? format(form.date, "PPP", { locale: es })
          : "";
      const timeLabel = form.time
        ? formatTime(form.time) + (form.end_time ? ` – ${formatTime(form.end_time)}` : "")
        : "";
      const res = await api.post("/events/flyer", {
        name: form.name,
        description: form.description,
        date_label: dateLabel,
        time_label: timeLabel,
        location: form.location,
      }, { timeout: 180000 });
      setFlyerOptions(res.data.options || []);
      setFlyerVerse(res.data.verse || null);
      toast.success("Opciones listas. Elige tu flyer favorito.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const chooseFlyer = (option) => {
    setForm((f) => ({ ...f, image_url: option.image_url }));
    setFlyerOptions([]);
    toast.success(`Flyer "${option.label}" seleccionado`);
  };

  const openNew = () => {
    setForm(empty);
    setEditingId(null);
    setFlyerOptions([]);
    setFlyerVerse(null);
    setOpen(true);
  };

  const openEdit = (ev) => {
    let dateObj;
    if (!ev.recurring && ev.date) {
      const [y, m, d] = ev.date.split("-").map(Number);
      dateObj = new Date(y, m - 1, d);
    }
    setForm({
      name: ev.name || "",
      date: dateObj,
      time: ev.time || "",
      end_time: ev.end_time || "",
      location: ev.location || "",
      description: ev.description || "",
      recurring: !!ev.recurring,
      weekday: ev.weekday !== null && ev.weekday !== undefined ? ev.weekday : undefined,
      image_url: ev.image_url || "",
      featured: !!ev.featured,
    });
    setEditingId(ev.id);
    setFlyerOptions([]);
    setFlyerVerse(null);
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name || !form.location) {
      toast.error("Nombre y lugar son obligatorios");
      return;
    }
    if (form.recurring && (form.weekday === undefined || form.weekday === null)) {
      toast.error("Selecciona el día de la semana para el evento recurrente");
      return;
    }
    if (!form.recurring && !form.date) {
      toast.error("Selecciona la fecha del evento");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      date: form.recurring ? "" : format(form.date, "yyyy-MM-dd"),
      time: form.time,
      end_time: form.end_time,
      location: form.location,
      description: form.description,
      recurring: form.recurring,
      weekday: form.recurring ? Number(form.weekday) : null,
      image_url: form.image_url,
      featured: form.featured,
    };
    try {
      if (editingId) {
        await api.put(`/events/${editingId}`, payload);
        toast.success("Evento actualizado");
      } else {
        await api.post("/events", payload);
        toast.success("Evento creado");
      }
      setForm(empty);
      setEditingId(null);
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      toast.success("Evento eliminado");
      reload();
    } catch (err) {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <div data-testid="events-panel">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-serif text-3xl">Eventos de la Iglesia</h2>
        <Button
          data-testid="new-event-button"
          onClick={openNew}
          className="rounded-full bg-primary text-primary-foreground hover:bg-[#E5B54B]"
        >
          <Plus className="mr-2 h-4 w-4" /> Nuevo Evento
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="border-border bg-card sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                {editingId ? "Editar Evento" : "Crear Evento"}
              </DialogTitle>
              <DialogDescription>
                {editingId ? "Modifica los datos del evento." : "Registra un nuevo evento de la iglesia."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Nombre</Label>
                <Input
                  data-testid="event-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Vigilia de Adoración"
                  className="bg-muted"
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo de evento</Label>
                <Select
                  value={form.recurring ? "recurring" : "once"}
                  onValueChange={(v) => setForm({ ...form, recurring: v === "recurring" })}
                >
                  <SelectTrigger data-testid="event-type-select" className="bg-muted">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-border bg-card">
                    <SelectItem value="once">Fecha única</SelectItem>
                    <SelectItem value="recurring">Recurrente (cada semana)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.recurring ? (
                <div className="space-y-2">
                  <Label>Día de la semana</Label>
                  <Select
                    value={form.weekday !== undefined && form.weekday !== null ? String(form.weekday) : ""}
                    onValueChange={(v) => setForm({ ...form, weekday: Number(v) })}
                  >
                    <SelectTrigger data-testid="event-weekday-select" className="bg-muted">
                      <SelectValue placeholder="Selecciona un día" />
                    </SelectTrigger>
                    <SelectContent className="border-border bg-card">
                      {WEEKDAYS_ES.map((d, i) => (
                        <SelectItem key={i} value={String(i)}>
                          {d}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Fecha</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        data-testid="event-date-trigger"
                        className="w-full justify-start border-border bg-muted font-normal hover:bg-muted"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 text-primary" />
                        {form.date ? format(form.date, "PPP", { locale: es }) : "Selecciona una fecha"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto border-border bg-card p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.date}
                        onSelect={(d) => setForm({ ...form, date: d })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Hora de inicio</Label>
                  <Input
                    type="time"
                    data-testid="event-time-input"
                    value={form.time}
                    onChange={(e) => setForm({ ...form, time: e.target.value })}
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hora de fin</Label>
                  <Input
                    type="time"
                    data-testid="event-endtime-input"
                    value={form.end_time}
                    onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                    className="bg-muted"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Lugar</Label>
                <Input
                  data-testid="event-location-input"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Templo Central"
                  className="bg-muted"
                />
              </div>
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea
                  data-testid="event-description-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Detalles del evento…"
                  className="bg-muted"
                />
              </div>

              <div className="space-y-2">
                <Label>Flyer / Imagen o Video (opcional)</Label>
                <div className="flex flex-wrap items-center gap-3">
                  <label
                    htmlFor="event-image-input"
                    data-testid="event-image-label"
                    className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-primary/40 bg-muted px-4 py-2 text-xs font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                  >
                    {optimizing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                    {optimizing ? "Optimizando…" : form.image_url ? "Cambiar archivo" : "Subir imagen o video"}
                  </label>
                  <input
                    id="event-image-input"
                    type="file"
                    accept="image/*,video/*"
                    data-testid="event-image-input"
                    className="hidden"
                    disabled={optimizing}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setOptimizing(true);
                      try {
                        const { dataUrl, mediaType } = await prepareMediaFile(file);
                        setForm((f) => ({ ...f, image_url: dataUrl }));
                        if (mediaType === "video") toast.success("Video optimizado y listo");
                      } catch (err) {
                        toast.error(err?.message || formatApiErrorDetail(err.response?.data?.detail));
                      } finally {
                        setOptimizing(false);
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={generateFlyer}
                    disabled={generating || optimizing}
                    data-testid="generate-flyer-button"
                    className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-2 text-xs font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground disabled:opacity-50"
                  >
                    {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    {generating ? "Generando opciones… (puede tardar 1 min)" : "Generar flyer con IA"}
                  </button>
                  {form.image_url && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, image_url: "" })}
                      data-testid="event-image-remove"
                      className="text-xs text-muted-foreground hover:text-destructive"
                    >
                      Quitar
                    </button>
                  )}
                </div>
                {flyerOptions.length > 0 && (
                  <div className="mt-3 space-y-2" data-testid="flyer-options">
                    <p className="text-xs text-muted-foreground">
                      Elige tu favorito{flyerVerse ? ` — versículo: ${flyerVerse.ref}` : ""}:
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {flyerOptions.map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => chooseFlyer(opt)}
                          data-testid={`flyer-option-${opt.key}`}
                          className="group relative overflow-hidden rounded-sm border border-border transition-all hover:border-primary hover:ring-1 hover:ring-primary"
                        >
                          <img src={opt.image_url} alt={opt.label} className="aspect-[2/3] w-full object-cover" />
                          <span className="absolute inset-x-0 bottom-0 bg-black/70 px-1 py-1 text-center text-[10px] font-semibold text-primary">
                            {opt.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {form.image_url && (
                  isVideoSrc(form.image_url) ? (
                    <video
                      src={form.image_url}
                      data-testid="event-image-preview"
                      className="mt-3 max-h-48 rounded-sm border border-border"
                      autoPlay
                      muted
                      loop
                      playsInline
                    />
                  ) : (
                    <img
                      src={form.image_url}
                      alt="Vista previa del flyer"
                      data-testid="event-image-preview"
                      className="mt-3 max-h-48 rounded-sm border border-border object-contain"
                    />
                  )
                )}
                <p className="text-[10px] text-muted-foreground">Los videos se optimizan automáticamente al subirlos (máx. 25 MB).</p>
              </div>

              <label className="flex cursor-pointer items-center justify-between rounded-sm border border-border bg-muted p-3">
                <span className="text-sm">
                  <Star className="mr-1.5 inline h-4 w-4 text-primary" />
                  Destacar como banner principal
                </span>
                <input
                  type="checkbox"
                  data-testid="event-featured-toggle"
                  checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  className="h-4 w-4 accent-[hsl(45_85%_55%)]"
                />
              </label>
            </div>
            <DialogFooter>
              <Button
                onClick={submit}
                disabled={saving}
                data-testid="save-event-button"
                className="rounded-full bg-primary text-primary-foreground hover:bg-[#E5B54B]"
              >
                {saving ? "Guardando…" : editingId ? "Actualizar Evento" : "Guardar Evento"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      {events.length === 0 ? (
        <Card className="border-dashed border-border bg-card p-16 text-center text-muted-foreground">
          No hay eventos. Crea el primero con el botón &quot;Nuevo Evento&quot;.
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {events.map((ev) => (
            <Card
              key={ev.id}
              data-testid={`event-card-${ev.id}`}
              className="group overflow-hidden border-border bg-card p-6 transition-colors hover:border-primary/50"
            >
              {ev.image_url && (
                isVideoSrc(ev.image_url) ? (
                  <video
                    src={ev.image_url}
                    className="mb-4 -mx-6 -mt-6 h-40 w-[calc(100%+3rem)] object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                ) : (
                  <img
                    src={ev.image_url}
                    alt={ev.name}
                    className="mb-4 -mx-6 -mt-6 h-40 w-[calc(100%+3rem)] object-cover"
                  />
                )
              )}
              {ev.featured && (
                <span className="mb-3 inline-flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                  <Star className="h-3 w-3" /> Destacado
                </span>
              )}
              <div className="flex items-start justify-between">
                <h3 className="font-serif text-2xl leading-tight">{ev.name}</h3>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => openEdit(ev)}
                    data-testid={`edit-event-${ev.id}`}
                    className="text-muted-foreground transition-colors hover:text-primary"
                    title="Editar evento"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        data-testid={`delete-event-${ev.id}`}
                        className="text-muted-foreground transition-colors hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="border-border bg-card">
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar evento?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Se eliminará &quot;{ev.name}&quot; y sus transacciones asociadas. Esta acción no se puede deshacer.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => remove(ev.id)}
                          data-testid={`confirm-delete-event-${ev.id}`}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Eliminar
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              {ev.description && (
                <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{ev.description}</p>
              )}
              <div className="mt-5 flex flex-wrap gap-4 border-t border-border pt-4 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  {ev.recurring ? (
                    <>
                      <Repeat className="h-3.5 w-3.5 text-primary" />{" "}
                      {ev.weekday !== null && ev.weekday !== undefined ? RECURRING_LABEL_ES[ev.weekday] : "Semanal"}
                    </>
                  ) : (
                    <>
                      <CalendarDays className="h-3.5 w-3.5 text-primary" /> {ev.date}
                    </>
                  )}
                </span>
                {ev.time && (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-primary" /> {formatTime(ev.time)}
                    {ev.end_time ? ` – ${formatTime(ev.end_time)}` : ""}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 text-primary" /> {ev.location}
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
